// Pure transform from depth bucket rows to multi-series line data for the
// depth-over-time chart. Sixteen series: {quoted,exec} × {bid,ask} ×
// {0.25%,0.5%,1%,2%} quote-notional. Quoted is touch-anchored (the
// contractual MM KPI); exec is mid-anchored DIRECTIONAL executable depth
// (LIQ-29, migration 013) — exec zero is economic (spread consumed the
// band), NULL is data-quality (incl. all pre-013 history).
// No echarts, no React — unit-tested with node:test (mirrors lib/boxwhisker.ts).
//
// Each point is [tsMs, notionalQuote | null]. null where that band was not
// measurable in that bucket (renders as a gap, never fabricated). Leading
// partial buckets (start before the window) are dropped, same as boxwhisker.
import type { DepthBucketRow } from "../api/types.ts";

export type Side = "bid" | "ask";
export type BandKey = "0p25" | "0p5" | "1p0" | "2p0";
// quoted (touch-anchored) vs executable (mid-anchored, directional) — LIQ-29.
export type Family = "quoted" | "exec";

export const SIDES: Side[] = ["bid", "ask"];
export const BANDS: BandKey[] = ["0p25", "0p5", "1p0", "2p0"];
export const FAMILIES: Family[] = ["quoted", "exec"];
export const BAND_PCT: Record<BandKey, string> = {
  "0p25": "0.25%",
  "0p5": "0.5%",
  "1p0": "1%",
  "2p0": "2%",
};

// stable series key + human label. Quoted keys keep the legacy shape
// ("bid_2p0" / "bid ±2%"); exec carries the family in key and label
// ("bid_exec_2p0" / "bid exec ±2%").
export function seriesKey(side: Side, band: BandKey, family: Family = "quoted"): string {
  return family === "exec" ? `${side}_exec_${band}` : `${side}_${band}`;
}
export function seriesLabel(side: Side, band: BandKey, family: Family = "quoted"): string {
  return family === "exec"
    ? `${side} exec ±${BAND_PCT[band]}`
    : `${side} ±${BAND_PCT[band]}`;
}

export type Point = [number, number | null];

export interface DepthSeries {
  key: string;
  side: Side;
  band: BandKey;
  family: Family;
  label: string;
  points: Point[];
}

export interface DepthWindow {
  lookbackMinutes: number;
  bucketSeconds: number;
  nowMs: number;
}

// Per-bucket status, for marking non-ok regions on the chart.
export interface StatusPoint {
  tsMs: number;
  status: string;
}

export interface DepthSeriesResult {
  series: DepthSeries[]; // always all sixteen: FAMILIES×SIDES×BANDS order
  statuses: StatusPoint[];
  populatedBuckets: number;
  okBuckets: number;
  expectedBuckets: number;
  windowStartMs: number;
  windowEndMs: number;
}

export function expectedBucketCount(
  lookbackMinutes: number,
  bucketSeconds: number,
): number {
  return Math.max(1, Math.floor((lookbackMinutes * 60) / bucketSeconds));
}

function notionalAt(
  row: DepthBucketRow,
  side: Side,
  band: BandKey,
  family: Family = "quoted",
): number | null {
  const r = row as unknown as Record<string, number | boolean | null>;
  const stem = family === "exec" ? `${side}_exec` : side;
  // a band that wasn't measurable carries null depth already; the measurable
  // flag is the authoritative gate, so honor it even if depth were non-null.
  // Exec measured-zero (spread consumed the band) is economic and PLOTS as 0;
  // NULL (incl. all pre-013 history, no backfill) renders as a gap.
  if (!r[`${stem}_measurable_${band}`]) return null;
  const v = r[`${stem}_notional_${band}_quote`];
  return typeof v === "number" ? v : null;
}

export function toDepthSeries(
  rows: DepthBucketRow[],
  w: DepthWindow,
): DepthSeriesResult {
  const windowStartMs = w.nowMs - w.lookbackMinutes * 60_000;
  const windowEndMs = w.nowMs;
  const sorted = [...rows].sort(
    (a, b) => Date.parse(a.bucket_start) - Date.parse(b.bucket_start),
  );

  const series: DepthSeries[] = [];
  for (const family of FAMILIES) {
    for (const side of SIDES) {
      for (const band of BANDS) {
        series.push({
          key: seriesKey(side, band, family),
          side,
          band,
          family,
          label: seriesLabel(side, band, family),
          points: [],
        });
      }
    }
  }
  const byKey = new Map(series.map((s) => [s.key, s]));

  const statuses: StatusPoint[] = [];
  let populated = 0;
  let ok = 0;
  for (const row of sorted) {
    const ts = Date.parse(row.bucket_start);
    if (Number.isNaN(ts) || ts < windowStartMs) continue; // leading partial / bad
    populated++;
    if (row.reconstruction_status === "ok") ok++;
    statuses.push({ tsMs: ts, status: row.reconstruction_status });
    for (const family of FAMILIES) {
      for (const side of SIDES) {
        for (const band of BANDS) {
          byKey
            .get(seriesKey(side, band, family))!
            .points.push([ts, notionalAt(row, side, band, family)]);
        }
      }
    }
  }

  const nominal = expectedBucketCount(w.lookbackMinutes, w.bucketSeconds);
  return {
    series,
    statuses,
    populatedBuckets: populated,
    okBuckets: ok,
    expectedBuckets: Math.max(nominal, populated),
    windowStartMs,
    windowEndMs,
  };
}

// True when there is nothing usable to plot (no buckets, or every point null).
export function allEmpty(result: DepthSeriesResult): boolean {
  if (result.populatedBuckets === 0) return true;
  return result.series.every((s) => s.points.every((p) => p[1] == null));
}

// Latest bucket's per-band cells for the ladder (bid+ask, all four quoted
// bands). Quoted family ONLY — exec is chart-only for now (LIQ-29).
export interface LadderRow {
  band: BandKey;
  label: string; // "±0.5%"
  bidQuote: number | null;
  askQuote: number | null;
  bidMeasurable: boolean;
  askMeasurable: boolean;
}

export function ladderRows(row: DepthBucketRow | null): LadderRow[] {
  return BANDS.map((band) => {
    const r = row as unknown as Record<string, number | boolean | null> | null;
    return {
      band,
      label: `±${BAND_PCT[band]}`,
      bidQuote: r ? notionalAt(row as DepthBucketRow, "bid", band) : null,
      askQuote: r ? notionalAt(row as DepthBucketRow, "ask", band) : null,
      bidMeasurable: r ? Boolean(r[`bid_measurable_${band}`]) : false,
      askMeasurable: r ? Boolean(r[`ask_measurable_${band}`]) : false,
    };
  });
}
